import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useBodyLock } from "../../hooks/useBodyLock";
import { rm, credits } from "../../lib/format";
import { ProductImage } from "../ProductCard";
import { Badge } from "../ui";
import { VaultActionsPanel, type VaultActionItem } from "./VaultActionsPanel";
import { FlexxiPlanPanel } from "./FlexxiPlanPanel";

export interface VaultItemDetail {
  id: string;
  name: string;
  image: string | null;
  estValue: number;
  buybackCredits: number | null;
  buybackPercent: number | null;
  packName: string | null;
  canBuyback: boolean;
  status: string;
  flexxiPlan?: { id: string; status: string } | null;
}

interface VaultItemModalProps {
  item: VaultItemDetail | null;
  vaultOn: boolean;
  tradingOn: boolean;
  creditsEnabled: boolean;
  onClose: () => void;
  onTradeCreated: () => void;
}

function toActionItem(item: VaultItemDetail): VaultActionItem {
  return {
    id: item.id,
    name: item.name,
    estValue: item.estValue,
    buybackCredits: item.buybackCredits,
    canBuyback: item.canBuyback,
  };
}

function buybackOfferLabel(percent: number | null): string {
  if (percent && percent > 0) return `Buyback Offer (${percent}% MV)`;
  return "Buyback Offer";
}

export function VaultItemModal({
  item,
  vaultOn,
  tradingOn,
  creditsEnabled,
  onClose,
  onTradeCreated,
}: VaultItemModalProps) {
  useBodyLock(!!item);

  const inVault = item?.status === "IN_VAULT";
  const flexxiHold = item?.status === "FLEXXI_HOLD";
  const inTrade = item?.status === "IN_TRADE";
  const showActions = Boolean(item && inVault && vaultOn);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={item.name}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm sm:items-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: "spring", damping: 28, stiffness: 360 }}
            className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border border-white/10 bg-surface shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white/80 backdrop-blur-sm transition-colors hover:bg-black/55"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <div className="overflow-y-auto">
              <div className="bg-gradient-to-b from-white/[0.04] to-transparent px-5 pb-4 pt-5">
                <ProductImage src={item.image} alt={item.name} className="mx-auto aspect-[5/7] w-full max-w-[220px] rounded-2xl shadow-xl" />
              </div>

              <div className="space-y-4 px-5 pb-5">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="text-center">
                    <h2 className="font-display text-base font-bold leading-snug text-white">{item.name}</h2>
                    {item.packName && <p className="mt-1 text-xs text-white/50">{item.packName}</p>}
                    {!inVault && (
                      <div className="mt-2 flex justify-center">
                        <Badge color={inTrade ? "pink" : item.status === "SHIPMENT_REQUESTED" ? "amber" : "gray"}>
                          {inTrade ? "In trade" : item.status.replace(/_/g, " ")}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-white/50">Market Value (MV)</span>
                      <span className="font-display font-bold text-white">{rm(item.estValue)}</span>
                    </div>
                    {item.canBuyback && item.buybackCredits ? (
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-white/50">{buybackOfferLabel(item.buybackPercent)}</span>
                        <span className="font-display font-bold text-emerald-300">{credits(item.buybackCredits)}</span>
                      </div>
                    ) : null}
                  </div>
                </div>

                {flexxiHold ? (
                  item.flexxiPlan?.id ? (
                    <FlexxiPlanPanel planId={item.flexxiPlan.id} showDashboardLink />
                  ) : (
                    <p className="rounded-2xl border border-line bg-hover/40 p-3 text-sm text-muted">
                      Instalment plan details are unavailable. Refresh the page or contact support if this persists.
                    </p>
                  )
                ) : null}

                {showActions ? (
                  <VaultActionsPanel
                    selectedItems={[toActionItem(item)]}
                    tradingOn={tradingOn}
                    creditsEnabled={creditsEnabled}
                    onClear={onClose}
                    onTradeCreated={onTradeCreated}
                    compact
                  />
                ) : null}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
